"use client";
import Link from "@tiptap/extension-link";
import { EditorContent, useEditor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import React, { useState } from "react";
import { Modal, Dialog as RACDialog } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import type { Size } from "../../utils/controlStyles";
import { Button } from "../Button";
import { DialogHeader } from "../DialogHeader";
import { Icon } from "../Icon";
import { ModalBackdrop } from "../ModalBackdrop";
import { TextField } from "../TextField";

const sizeConfig = {
  sm: {
    toolbar: "p-1.5",
    contentWrapper: "min-h-[100px]",
    contentPadding: "p-3",
    placeholderPos: "left-3 top-3",
    prose: "prose-xs",
    h1: "[&_.ProseMirror_h1]:mb-2 [&_.ProseMirror_h1]:text-base [&_.ProseMirror_h1]:font-bold",
    h2: "[&_.ProseMirror_h2]:mb-1.5 [&_.ProseMirror_h2]:text-sm [&_.ProseMirror_h2]:font-semibold",
    p: "[&_.ProseMirror_p]:mb-1",
    list: "[&_.ProseMirror_ul]:mb-1 [&_.ProseMirror_ul]:ml-4 [&_.ProseMirror_ol]:mb-1 [&_.ProseMirror_ol]:ml-4",
    li: "[&_.ProseMirror_li]:mb-0.5",
    placeholderText: "text-xs",
  },
  md: {
    toolbar: "p-2",
    contentWrapper: "min-h-[200px]",
    contentPadding: "p-4",
    placeholderPos: "left-4 top-4",
    prose: "prose-sm",
    h1: "[&_.ProseMirror_h1]:mb-4 [&_.ProseMirror_h1]:text-2xl [&_.ProseMirror_h1]:font-bold",
    h2: "[&_.ProseMirror_h2]:mb-3 [&_.ProseMirror_h2]:text-xl [&_.ProseMirror_h2]:font-semibold",
    p: "[&_.ProseMirror_p]:mb-2",
    list: "[&_.ProseMirror_ul]:mb-2 [&_.ProseMirror_ul]:ml-6 [&_.ProseMirror_ol]:mb-2 [&_.ProseMirror_ol]:ml-6",
    li: "[&_.ProseMirror_li]:mb-1",
    placeholderText: "text-sm",
  },
  lg: {
    toolbar: "p-2.5",
    contentWrapper: "min-h-[250px]",
    contentPadding: "p-5",
    placeholderPos: "left-5 top-5",
    prose: "prose-base",
    h1: "[&_.ProseMirror_h1]:mb-5 [&_.ProseMirror_h1]:text-3xl [&_.ProseMirror_h1]:font-bold",
    h2: "[&_.ProseMirror_h2]:mb-4 [&_.ProseMirror_h2]:text-2xl [&_.ProseMirror_h2]:font-semibold",
    p: "[&_.ProseMirror_p]:mb-3",
    list: "[&_.ProseMirror_ul]:mb-3 [&_.ProseMirror_ul]:ml-6 [&_.ProseMirror_ol]:mb-3 [&_.ProseMirror_ol]:ml-6",
    li: "[&_.ProseMirror_li]:mb-1.5",
    placeholderText: "text-base",
  },
  xl: {
    toolbar: "p-3",
    contentWrapper: "min-h-[300px]",
    contentPadding: "p-6",
    placeholderPos: "left-6 top-6",
    prose: "prose-lg",
    h1: "[&_.ProseMirror_h1]:mb-6 [&_.ProseMirror_h1]:text-4xl [&_.ProseMirror_h1]:font-bold",
    h2: "[&_.ProseMirror_h2]:mb-5 [&_.ProseMirror_h2]:text-3xl [&_.ProseMirror_h2]:font-semibold",
    p: "[&_.ProseMirror_p]:mb-3",
    list: "[&_.ProseMirror_ul]:mb-3 [&_.ProseMirror_ul]:ml-8 [&_.ProseMirror_ol]:mb-3 [&_.ProseMirror_ol]:ml-8",
    li: "[&_.ProseMirror_li]:mb-2",
    placeholderText: "text-lg",
  },
} satisfies Record<Size, Record<string, string>>;

type HeadingLevel = 1 | 2;

export interface RichTextEditorProps {
  /**
   * The initial content of the editor
   */
  initialContent?: string;
  /**
   * Callback when the content changes
   */
  onChange?: (html: string) => void;
  /**
   * Additional className to apply to the editor
   */
  className?: string;
  /**
   * Whether the editor is disabled
   */
  isDisabled?: boolean;
  /**
   * Placeholder text when editor is empty
   */
  placeholder?: string;
  /**
   * ID for the editor element for label association
   */
  id?: string;
  /**
   * Size variant controlling toolbar padding, content area height, and typography scale
   */
  size?: Size;
  /**
   * Which heading levels to enable in the toolbar and editor.
   * @default [1, 2]
   */
  headingLevels?: HeadingLevel[];
}

const ToolbarButton = ({
  isActive = false,
  onPress,
  children,
}: {
  isActive?: boolean;
  onPress: () => void;
  children: React.ReactNode;
}) => (
  <Button variant={isActive ? "secondary" : "ghost"} size="sm" onPress={onPress} className="p-1">
    {children}
  </Button>
);

interface LinkDialogProps {
  initialUrl: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (url: string) => void;
}

function LinkDialog({ initialUrl, isOpen, onClose, onSubmit }: LinkDialogProps) {
  const [url, setUrl] = useState(initialUrl);

  React.useEffect(() => {
    setUrl(initialUrl);
  }, [initialUrl]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(url);
    onClose();
  };

  return (
    <ModalBackdrop isDismissable isOpen={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Modal className="flex items-center justify-center p-4 outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0">
        <RACDialog className="relative bg-background-surface rounded-lg shadow-xl max-w-md w-full">
          <form onSubmit={handleSubmit}>
            <DialogHeader title="Insert Link" onClose={onClose} />
            <div className="p-6">
              <TextField label="URL" type="url" value={url} onChange={setUrl} autoFocus className="w-full" />
            </div>
            <div className="flex justify-end gap-2 border-t border-border-muted p-4">
              <Button variant="secondary" onPress={onClose}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </div>
          </form>
        </RACDialog>
      </Modal>
    </ModalBackdrop>
  );
}

/**
 * RichTextEditor
 *
 * A rich text editor with formatting capabilities including headings, bold, italic, lists, and links.
 * Built on top of TipTap/ProseMirror.
 */
export function RichTextEditor({
  initialContent = "",
  onChange,
  className,
  isDisabled = false,
  placeholder = "Start typing...",
  id,
  size = "md",
  headingLevels = [1, 2],
}: RichTextEditorProps) {
  const sc = sizeConfig[size];
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: headingLevels.length > 0 ? { levels: headingLevels } : false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class:
            "text-text-link-default hover:text-text-link-hover underline decoration-text-link-default/30 hover:decoration-text-link-hover cursor-pointer",
        },
      }),
    ],
    content: initialContent,
    editable: !isDisabled,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: twMerge(
          `prose ${sc.prose} max-w-none`,
          "focus:outline-none",
          "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
        ),
      },
    },
  });

  const handleLinkClick = () => {
    setIsLinkDialogOpen(true);
  };

  const handleLinkSubmit = (url: string) => {
    if (url === "") {
      editor?.chain().focus().unsetLink().run();
    } else {
      editor?.chain().focus().setLink({ href: url }).run();
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <div
      id={id}
      data-richtexteditor
      className={twMerge(
        "flex flex-col rounded-md border border-border-input bg-background-input",
        "outline-none focus-within:outline-none focus-within:border-border-input focus-within:ring-0 focus-within:shadow-none",
        isDisabled && "opacity-50",
        className
      )}
      style={{ outline: "none" }}
    >
      {!isDisabled && editor && (
        <>
          <BubbleMenu
            editor={editor}
            shouldShow={({ editor }) => editor.isActive("link") && !isLinkDialogOpen}
            className="flex items-center gap-1 rounded-md border border-border-input bg-background-surface p-1 shadow-md"
          >
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onPress={() => {
                const href = editor.getAttributes("link").href;
                if (!href) return;
                window.open(href, "_blank", "noopener,noreferrer");
              }}
            >
              <Icon name="ArrowSquareOut" className="mr-1 h-4 w-4" />
              Open
            </Button>
            <Button variant="ghost" size="sm" className="h-8 px-2" onPress={handleLinkClick}>
              <Icon name="PencilSimple" className="mr-1 h-4 w-4" />
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onPress={() => editor.chain().focus().unsetLink().run()}
            >
              <Icon name="X" className="mr-1 h-4 w-4" />
              Remove
            </Button>
          </BubbleMenu>

          <LinkDialog
            isOpen={isLinkDialogOpen}
            onClose={() => setIsLinkDialogOpen(false)}
            onSubmit={handleLinkSubmit}
            initialUrl={editor.getAttributes("link").href || ""}
          />
        </>
      )}
      <div className={twMerge("flex items-center gap-1 border-b border-border-input", sc.toolbar)}>
        {headingLevels.map((level) => (
          <ToolbarButton
            key={level}
            isActive={editor.isActive("heading", { level })}
            onPress={() => editor.chain().focus().toggleHeading({ level }).run()}
          >
            <span className="text-xs font-bold">H{level}</span>
          </ToolbarButton>
        ))}
        <ToolbarButton isActive={editor.isActive("bold")} onPress={() => editor.chain().focus().toggleBold().run()}>
          <span className="text-xs font-bold">B</span>
        </ToolbarButton>
        <ToolbarButton isActive={editor.isActive("italic")} onPress={() => editor.chain().focus().toggleItalic().run()}>
          <span className="text-xs italic">I</span>
        </ToolbarButton>
        <div className="mx-1 h-4 w-px bg-border-muted" />
        <ToolbarButton
          isActive={editor.isActive("bulletList")}
          onPress={() => editor.chain().focus().toggleBulletList().run()}
        >
          <Icon name="ListBullets" className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          isActive={editor.isActive("orderedList")}
          onPress={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <Icon name="ListNumbers" className="h-4 w-4" />
        </ToolbarButton>
        <div className="mx-1 h-4 w-px bg-border-muted" />
        <ToolbarButton isActive={editor.isActive("link")} onPress={handleLinkClick}>
          <Icon name="PaperclipHorizontal" className="h-4 w-4" />
        </ToolbarButton>
      </div>
      <div className={twMerge("relative w-full outline-none focus:outline-none", sc.contentWrapper)}>
        <EditorContent
          editor={editor}
          className={twMerge(
            "w-full outline-none focus:outline-none",
            sc.contentPadding,
            "[&_.ProseMirror]:min-h-[inherit]",
            "[&_.ProseMirror]:outline-none [&_.ProseMirror]:focus:outline-none",
            sc.h1,
            sc.h2,
            sc.p,
            "[&_.ProseMirror_ul]:list-disc",
            "[&_.ProseMirror_ol]:list-decimal",
            sc.list,
            sc.li,
            "[&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-border-muted [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:italic",
            "[&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:bg-background-muted [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:py-0.5 [&_.ProseMirror_code]:font-mono [&_.ProseMirror_code]:text-sm",
            "[&_.ProseMirror_pre]:rounded-md [&_.ProseMirror_pre]:bg-background-muted [&_.ProseMirror_pre]:p-3 [&_.ProseMirror_pre]:font-mono [&_.ProseMirror_pre]:text-sm",
            "[&_.ProseMirror_hr]:border-border-muted",
            isDisabled && "pointer-events-none"
          )}
        />
        {editor.isEmpty && !isDisabled && (
          <div
            className={twMerge(
              "pointer-events-none absolute text-text-secondary",
              sc.placeholderPos,
              sc.placeholderText
            )}
          >
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}
